import { DEFAULT_DATA, StorageData } from '~Components/Storage';

/**
 * Storage keys that are bound to this specific device, venue or runtime state and
 * therefore must never be copied into, or applied from, a blueprint.
 */
export const EXCLUDED_BLUEPRINT_KEYS: ReadonlyArray<keyof StorageData> = [
  'currency',
  'metadata',
  'adyenTerminals',
  'terminalNames',
  'ordersForKivraSync',
  'deviceId',
  'clientId',
  'taxComplianceDeviceId',
  'taxComplianceDeviceName',
  'taxComplianceControlUnitSerial',
  'taxComplianceProductionNumber',
  'taxComplianceCorporateId',
  'storedOrders',
  'pendingOrderSyncs',
  // Hotel room charges in progress on this device
  'hotelRoomCharges',
  'hotelReopenedCheques',
  // Only set by the manual browser link flow
  'allowBrowserClick',
  'autoAcceptAppOrders',
  // Which blueprint this device applied is a device record, not a setting
  'chosenBlueprint',
  // Which POS scene this device last opened is runtime state, not a setting
  'lastUsedPosMode',
  'selectedLanguage',
  'zoneId',
  'selectedKDSStation',
  'KDSOtherStationsFilter',
  'devMode',
  'enableLogRocket',
  'useHandheldPrinter',
  'useHandheldBongPrinter',
];

export type BlueprintStorage = Partial<StorageData>;

/**
 * Strips the excluded keys so only transferable settings remain. Keys no longer in
 * DEFAULT_DATA are dropped too, since persisted storage keeps removed settings around.
 */
export const toBlueprintStorage = (storage: StorageData): BlueprintStorage => {
  const result: Record<string, unknown> = {};

  for (const key of Object.keys(storage) as Array<keyof StorageData>) {
    const known = key in DEFAULT_DATA;
    if (known && !EXCLUDED_BLUEPRINT_KEYS.includes(key)) result[key] = storage[key];
  }

  return result as BlueprintStorage;
};

/**
 * Sanitises a blueprint fetched from the backend before it is applied. Unknown
 * keys and excluded keys are dropped so a stale blueprint can't poison storage.
 */
export const fromBlueprintStorage = (raw: unknown): BlueprintStorage => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const known = key in DEFAULT_DATA;
    const excluded = EXCLUDED_BLUEPRINT_KEYS.includes(key as keyof StorageData);
    if (known && !excluded && value !== undefined) result[key] = value;
  }

  return result as BlueprintStorage;
};

export interface BlueprintChange {
  key: keyof StorageData;
  from: unknown;
  to: unknown;
}

/** JSON with object keys sorted so two equal objects stringify identically regardless of key order */
export const stableStringify = (value: unknown): string => {
  if (value === undefined) {
    return 'undefined';
  }

  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  const entries = Object.keys(value as Record<string, unknown>)
    .sort()
    .map(
      (key) =>
        `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`,
    );

  return `{${entries.join(',')}}`;
};

/** Keys in the blueprint whose value differs from what the device currently has, compared by content not reference */
export const diffBlueprintStorage = (
  current: StorageData,
  blueprint: BlueprintStorage,
): BlueprintChange[] => {
  const changes: BlueprintChange[] = [];

  for (const key of Object.keys(blueprint) as Array<keyof StorageData>) {
    const from = current[key];
    const to = blueprint[key];
    if (stableStringify(from) !== stableStringify(to)) changes.push({ key, from, to });
  }

  return changes;
};

/**
 * True when the blueprint holds exactly the settings a new blueprint from this device would.
 * Unlike diffBlueprintStorage this also checks keys the blueprint lacks, so a blueprint saved
 * before a setting existed is compared on it too, with the missing value read as its default.
 */
export const isSameBlueprintStorage = (
  current: BlueprintStorage,
  blueprint: BlueprintStorage,
): boolean => {
  const keys = new Set([...Object.keys(current), ...Object.keys(blueprint)]) as Set<
    keyof StorageData
  >;

  for (const key of keys) {
    const stored = key in blueprint ? blueprint[key] : DEFAULT_DATA[key];
    if (stableStringify(current[key]) !== stableStringify(stored)) return false;
  }

  return true;
};

/** Short, non-technical rendering of a storage value; booleans become the given on/off labels */
export const formatBlueprintValue = (
  value: unknown,
  labels: { on: string; off: string; empty: string },
): string => {
  if (typeof value === 'boolean') {
    return value ? labels.on : labels.off;
  }

  if (value === undefined || value === null || value === '') {
    return labels.empty;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  const text = stableStringify(value);
  return text.length > 24 ? `${text.slice(0, 21)}…` : text;
};
